import { verifyCrash, verifyMines, verifyPlinko } from "../../../packages/fairness-verifier/src/index.js";
import type { VerificationCheck, VerificationResult } from "../../../packages/fairness-verifier/src/index.js";

type JsonObject = Record<string, unknown>;
type Proof = {
  proof_version: string;
  game_id: string;
  round_id: string;
  rules_version: string;
  algorithm: string;
  commitment: { id: string; server_seed_hash: string; committed_at: string };
  inputs: JsonObject;
  reveal: { server_seed: string; revealed_at: string } | null;
  recorded_result: JsonObject;
};

const SUPPORTED_RULES_VERSION = "edge-1.0.0";

const byId = <T extends HTMLElement>(id: string): T => {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing element: ${id}`);
  return element as T;
};

const proofJson = byId<HTMLTextAreaElement>("proofJson");
const errorMessage = byId<HTMLParagraphElement>("errorMessage");
const overallStatus = byId<HTMLDivElement>("overallStatus");
const algorithmName = byId<HTMLSpanElement>("algorithmName");
const checksElement = byId<HTMLDivElement>("checks");
const metadata = byId<HTMLDListElement>("metadata");
const boardSection = byId<HTMLElement>("boardSection");
const minesBoard = byId<HTMLDivElement>("minesBoard");
const gameIdInput = byId<HTMLSelectElement>("gameId");
const roundIdInput = byId<HTMLInputElement>("roundId");
let proofToken = "";

function object(value: unknown, field: string): JsonObject {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${field} 格式錯誤`);
  return value as JsonObject;
}

function string(value: unknown, field: string): string {
  if (typeof value !== "string" || !value) throw new Error(`${field} 必須是字串`);
  return value;
}

function integer(value: unknown, field: string): number {
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) throw new Error(`${field} 必須是整數`);
  return parsed;
}

function numbers(value: unknown, field: string): number[] {
  if (!Array.isArray(value) || !value.every(item => Number.isInteger(item))) throw new Error(`${field} 必須是整數陣列`);
  return value as number[];
}

function parseProof(value: string): Proof {
  const parsed = object(JSON.parse(value), "proof");
  const commitment = object(parsed.commitment, "commitment");
  const reveal = parsed.reveal === null ? null : object(parsed.reveal, "reveal");
  return {
    proof_version: string(parsed.proof_version, "proof_version"),
    game_id: string(parsed.game_id, "game_id"),
    round_id: string(parsed.round_id, "round_id"),
    rules_version: string(parsed.rules_version, "rules_version"),
    algorithm: string(parsed.algorithm, "algorithm"),
    commitment: {
      id: string(commitment.id, "commitment.id"),
      server_seed_hash: string(commitment.server_seed_hash, "commitment.server_seed_hash"),
      committed_at: string(commitment.committed_at, "commitment.committed_at")
    },
    inputs: object(parsed.inputs, "inputs"),
    reveal: reveal ? {
      server_seed: string(reveal.server_seed, "reveal.server_seed"),
      revealed_at: string(reveal.revealed_at, "reveal.revealed_at")
    } : null,
    recorded_result: object(parsed.recorded_result, "recorded_result")
  };
}

function setStatus(text: string, tone: "idle" | "ok" | "bad" | "wait"): void {
  overallStatus.textContent = text;
  overallStatus.dataset.tone = tone;
}

function showError(message: string): void {
  errorMessage.textContent = message;
  errorMessage.hidden = false;
  setStatus("無法驗證", "bad");
}

function clearError(): void {
  errorMessage.hidden = true;
  errorMessage.textContent = "";
}

function renderMetadata(proof: Proof): void {
  const values = [proof.game_id, proof.round_id, String(proof.inputs.nonce ?? "—"), proof.rules_version];
  [...metadata.querySelectorAll("dd")].forEach((element, index) => { element.textContent = values[index] ?? "—"; });
  algorithmName.textContent = proof.algorithm;
}

const labels: Record<VerificationCheck["key"], string> = {
  commitment: "伺服器種子承諾",
  outcome: "遊戲結果重算",
  payout: "倍率與派彩重算"
};

function renderChecks(result: VerificationResult<unknown>): void {
  checksElement.replaceChildren();
  for (const check of result.checks) {
    const card = document.createElement("div");
    card.className = "check";
    card.dataset.ok = String(check.ok);
    const head = document.createElement("div");
    head.className = "check-head";
    const title = document.createElement("h3");
    title.textContent = labels[check.key];
    const status = document.createElement("strong");
    status.textContent = check.ok ? "相符 ✓" : "不相符 ✕";
    const detail = document.createElement("code");
    detail.textContent = `紀錄 ${JSON.stringify(check.expected)}\n重算 ${JSON.stringify(check.actual)}`;
    head.append(title, status);
    card.append(head, detail);
    checksElement.append(card);
  }
}

function renderMinesBoard(minePositions: readonly number[]): void {
  minesBoard.replaceChildren();
  const mineSet = new Set(minePositions);
  for (let tile = 0; tile < 25; tile += 1) {
    const cell = document.createElement("div");
    const mine = mineSet.has(tile);
    cell.className = `tile${mine ? " is-mine" : ""}`;
    cell.textContent = mine ? "●" : "◆";
    cell.title = `${tile + 1}：${mine ? "地雷" : "寶石"}`;
    cell.setAttribute("aria-label", cell.title);
    minesBoard.append(cell);
  }
  boardSection.hidden = false;
}

async function verify(proof: Proof): Promise<void> {
  renderMetadata(proof);
  boardSection.hidden = true;
  if (proof.proof_version !== "sha-proof/1") throw new Error(`不支援 proof version：${proof.proof_version}`);
  if (proof.rules_version !== SUPPORTED_RULES_VERSION) {
    throw new Error(`不支援 rules version：${proof.rules_version}`);
  }
  if (!proof.reveal) {
    checksElement.innerHTML = '<div class="empty-state">回合尚未結算，server seed 尚未揭露。現在只能確認預先公布的 commitment。</div>';
    setStatus("尚未揭露", "wait");
    return;
  }
  let verified: VerificationResult<unknown>;
  if (proof.game_id === "mines" && proof.algorithm === "SHA256-FISHER-YATES-v1") {
    const payout = object(proof.recorded_result.payout, "recorded_result.payout");
    const revealedTiles = numbers(proof.recorded_result.revealed_tiles, "recorded_result.revealed_tiles");
    const status = string(proof.recorded_result.status, "recorded_result.status");
    if (status !== "won" && status !== "lost") throw new Error("MINES 結算狀態不正確");
    const result = await verifyMines({
      serverSeed: proof.reveal.server_seed,
      serverSeedHash: proof.commitment.server_seed_hash,
      clientSeed: string(proof.inputs.client_seed, "inputs.client_seed"),
      nonce: integer(proof.inputs.nonce, "inputs.nonce"),
      mines: integer(proof.inputs.mines, "inputs.mines"),
      recordedMinePositions: numbers(proof.recorded_result.mine_positions, "recorded_result.mine_positions"),
      settlement: {
        status,
        wagerUnits: integer(object(proof.inputs.wager, "inputs.wager").units, "inputs.wager.units"),
        picks: revealedTiles.length,
        recordedMultiplier: string(proof.recorded_result.multiplier, "recorded_result.multiplier"),
        recordedPayoutUnits: integer(payout.units, "recorded_result.payout.units")
      }
    });
    verified = result;
    renderMinesBoard(result.calculated.minePositions);
  } else if (proof.game_id === "plinko" && proof.algorithm === "HMAC-SHA256-PATH-v1") {
    const payout = object(proof.recorded_result.payout, "recorded_result.payout");
    const wager = object(proof.inputs.wager, "inputs.wager");
    verified = await verifyPlinko({
      serverSeed: proof.reveal.server_seed,
      serverSeedHash: proof.commitment.server_seed_hash,
      roundId: proof.round_id,
      clientSeed: string(proof.inputs.client_seed, "inputs.client_seed"),
      nonce: integer(proof.inputs.nonce, "inputs.nonce"),
      risk: string(proof.inputs.risk, "inputs.risk") as "low" | "medium" | "high",
      rows: integer(proof.inputs.rows, "inputs.rows"),
      wagerUnits: integer(wager.units, "inputs.wager.units"),
      recordedPath: numbers(proof.recorded_result.path, "recorded_result.path"),
      recordedSlot: integer(proof.recorded_result.slot, "recorded_result.slot"),
      recordedMultiplier: string(proof.recorded_result.multiplier, "recorded_result.multiplier"),
      recordedPayoutUnits: integer(payout.units, "recorded_result.payout.units")
    });
  } else if (proof.game_id === "crash" && proof.algorithm === "SHA256-CRASH-v1") {
    const wager = object(proof.inputs.wager, "inputs.wager");
    const payout = object(proof.recorded_result.payout, "recorded_result.payout");
    const cashedAt = proof.recorded_result.cashed_at;
    verified = await verifyCrash({
      serverSeed: proof.reveal.server_seed,
      serverSeedHash: proof.commitment.server_seed_hash,
      roundId: proof.round_id,
      recordedCrashMultiplier: string(proof.recorded_result.crash_multiplier, "recorded_result.crash_multiplier"),
      settlement: {
        wagerUnits: integer(wager.units, "inputs.wager.units"),
        cashedAt: cashedAt === null ? null : string(cashedAt, "recorded_result.cashed_at"),
        recordedPayoutUnits: integer(payout.units, "recorded_result.payout.units")
      }
    });
  } else {
    throw new Error(`尚未支援 ${proof.game_id} / ${proof.algorithm}`);
  }
  renderChecks(verified);
  setStatus(verified.ok ? "全部相符" : "驗證不符", verified.ok ? "ok" : "bad");
}

async function verifyTextarea(): Promise<void> {
  clearError();
  try { await verify(parseProof(proofJson.value)); }
  catch (caught) { showError(caught instanceof Error ? caught.message : String(caught)); }
}

async function loadFixture(): Promise<void> {
  clearError();
  try {
    const response = await fetch("./fixtures/mines-settled.json", { cache: "no-store" });
    if (!response.ok) throw new Error(`範例載入失敗：${response.status}`);
    proofJson.value = JSON.stringify(await response.json(), null, 2);
    await verifyTextarea();
  } catch (caught) { showError(caught instanceof Error ? caught.message : String(caught)); }
}

async function loadFromApi(): Promise<void> {
  clearError();
  const apiBase = byId<HTMLInputElement>("apiBase").value.replace(/\/$/, "");
  const gameId = gameIdInput.value;
  const playerId = byId<HTMLInputElement>("playerId").value.trim();
  const roundId = roundIdInput.value.trim();
  if (!apiBase || !gameId || !roundId || (!proofToken && !playerId)) {
    return showError("請填入 API 位址、遊戲、回合編號，以及驗證授權");
  }
  try {
    const response = await fetch(`${apiBase}/api/v1/games/${encodeURIComponent(gameId)}/rounds/${encodeURIComponent(roundId)}/proof`, {
      headers: proofToken ? { authorization: `Bearer ${proofToken}` } : { "x-player-id": playerId },
      cache: "no-store"
    });
    const payload = await response.json() as unknown;
    if (!response.ok) throw new Error(`API ${response.status}：${JSON.stringify(payload)}`);
    proofJson.value = JSON.stringify(payload, null, 2);
    await verifyTextarea();
  } catch (caught) { showError(caught instanceof Error ? caught.message : String(caught)); }
}

function applyEntryParameters(): void {
  if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
    byId<HTMLInputElement>("apiBase").value = "http://127.0.0.1:8791";
  }
  const parameters = new URLSearchParams(window.location.search);
  const gameId = parameters.get("game_id");
  const roundId = parameters.get("round_id");
  proofToken = parameters.get("token") ?? "";
  if (gameId && [...gameIdInput.options].some(option => option.value === gameId)) gameIdInput.value = gameId;
  if (roundId) roundIdInput.value = roundId;
  if (gameId || roundId) gameIdInput.closest("details")?.setAttribute("open", "");
  if (proofToken) {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete("token");
    window.history.replaceState(null, "", cleanUrl);
  }
  if (gameId && roundId && proofToken) void loadFromApi();
}

byId<HTMLButtonElement>("verifyProof").addEventListener("click", () => { void verifyTextarea(); });
byId<HTMLButtonElement>("loadFixture").addEventListener("click", () => { void loadFixture(); });
byId<HTMLButtonElement>("loadProof").addEventListener("click", () => { void loadFromApi(); });
applyEntryParameters();
